import {
  InsertItemsAction,
  FetchSuccessDedupeAction,
  DeleteItemsAction,
  OffsetStream,
} from './types';
import {
  handleDeleteItemsAction as handleDeleteItemsActionBase,
  handleFetchSuccessAction,
} from '../base/actionHandlers';

/**
 * Inserts a set of items into the stream at a given offset
 *
 * IMPORTANT: We do best guess with nextOffset tracking, as its
 * backend dependent please think about using this before using it!
 * Some backends or UX cases might not handle our simple method of
 * moving the next offset pointer forward.
 *
 * This is also not available for cursor based things because there
 * is no way to cleanly increment the offset.
 *
 * @param streamState
 * @param action
 */
export const handleInsertItemsAction = (
  streamState: OffsetStream,
  action: InsertItemsAction
): OffsetStream => {
  let nextOffset = streamState.nextOffset;
  const shouldUpdateNextOffset = !action.doNotUpdateNextOffset;
  if (shouldUpdateNextOffset && nextOffset !== undefined) {
    nextOffset += action.items.length;
  }
  const total =
    streamState.total !== undefined
      ? streamState.total + action.items.length
      : undefined;

  // Prepend - optimisation concat faster than slice and spread
  if (action.offset === 0) {
    return {
      ...streamState,
      nextOffset,
      items: action.items.concat(streamState.items || []),
      total,
    };
  }
  // Append - optimisation concat faster than slice and spread
  if (action.offset === streamState.items.length) {
    return {
      ...streamState,
      nextOffset,
      items: (streamState.items || []).concat(action.items),
      total,
    };
  }
  // Inject
  const current = streamState.items || [];
  const newItems = [
    ...current.slice(0, action.offset),
    ...action.items,
    ...current.slice(action.offset),
  ];
  return {
    ...streamState,
    nextOffset,
    items: newItems,
    total,
  };
};

/**
 * Merges in fetch results. This operates under the assumption that
 * there are no duplicate items in the stream; and if there are dupes
 * in the fetched stream they must be the result of accidental double-fetching
 * and can be safely discarded.
 *
 * IMPORTANT: do not use on streams that can possibly hold duplicates!
 * If unsure, do not use!
 *
 * @param streamState
 * @param action
 */
export const dedupeFetchResults = (
  streamState: OffsetStream,
  action: FetchSuccessDedupeAction
): OffsetStream => {
  const { items = [] } = streamState;
  const {
    items: actionItems,
    dedupeFunction = (item, existingItems) => !existingItems.includes(item),
    nextOffset,
    prevOffset,
    ...baseAction
  } = action;

  if (
    streamState.nextOffset &&
    nextOffset &&
    nextOffset <= streamState.nextOffset
  ) {
    // we already got all we had in this fetch
    return streamState;
  }

  // we want to dedupe already existing items from the stream
  const filteredItems = actionItems?.filter(item =>
    dedupeFunction(item, items)
  );

  return {
    ...handleFetchSuccessAction(streamState, {
      ...baseAction,
      items: filteredItems,
    }),

    nextOffset,
    prevOffset,
  };
};

export const handleDeleteItemsAction = (
  streamState: OffsetStream,
  action: DeleteItemsAction
): OffsetStream => {
  const { items, nextOffset = null } = streamState;
  const prevItemsCount = items.length;
  const newStreamState = handleDeleteItemsActionBase(streamState, action);
  const removedItemsCount = prevItemsCount - newStreamState.items.length;

  const shouldUpdateNextOffset = !action.doNotUpdateNextOffset;
  if (shouldUpdateNextOffset && nextOffset !== null) {
    return {
      ...newStreamState,
      nextOffset: nextOffset - removedItemsCount,
    };
  }
  return newStreamState;
};
